import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import PWAInstallManager from './components/PWAInstallManager';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { connect, createLocalTracks } from 'twilio-video';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Progress } from './components/ui/progress';
import { Separator } from './components/ui/separator';
import { Heart, Video, Clock, Users, Sparkles, MessageCircle, Lock, Mail, User, Eye, EyeOff, CheckCircle } from 'lucide-react';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import ShareButton from './components/ShareButton';
import NotificationSettings from './components/NotificationSettings';
import NotificationsPage from './components/NotificationsPage';
import QueuePage from './components/QueuePage';
import PWAStatus from './components/PWAStatus';
import PWASettings from './components/PWASettings';
import ReferralHandler from './components/ReferralHandler';
import SubscriptionPlans from './components/SubscriptionPlans';
import PaymentSuccess from './components/PaymentSuccess';
import UpgradeModal from './components/UpgradeModal';
import SubscriptionStatus from './components/SubscriptionStatus';
import AdAnalyticsDashboard from './components/AdAnalyticsDashboard';
import { PersonalAnalyticsDashboard, MatchSuccessAnalytics } from './components/AnalyticsDashboard';
import CreditDashboard from './components/CreditDashboard';
import CreditSuccess from './components/CreditSuccess';
import CreditCancel from './components/CreditCancel';
import { HomepageAd, ProfileCreationAd, SpeedDatingBreakAd, IcebreakersAd, MatchResultsAd, DateRecommendationsAd } from './components/AdPlacements';
import EnhancedHomepage from './components/EnhancedHomepage';
import GlobalNavigation from './components/GlobalNavigation';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import ScrollToTop from './components/ScrollToTop';
import SocialMediaFooter from './components/SocialMediaFooter';
import EnhancedButton from './components/EnhancedButton';
import AdvancedFeaturesDemo from './components/AdvancedFeaturesDemo';
import EnhancedHowItWorks from './components/EnhancedHowItWorks';
import SocialMediaIntegration from './components/SocialMediaIntegration';
import FacebookAuthCallback from './components/FacebookAuthCallback';
import SpeedDatingDemo from './components/SpeedDatingDemo';
import MatchingPreferences from './components/MatchingPreferences';
import InviteFriends from './components/InviteFriends';
import MatchingInterface from './components/MatchingInterface';
import YourConnections from './components/YourConnections';
import MatchProfile from './components/MatchProfile';
import Chat from './components/Chat';
import Conversations from './components/Conversations';
import BillingDashboard from './components/BillingDashboard';
import SubscriptionManager from './components/SubscriptionManager';
import NotificationCenter from './components/NotificationCenter';
import NotificationPreferences from './components/NotificationPreferences';
import { ToastManager } from './components/ToastNotification';
import { 
  EnhancedTimer, 
  AIIcebreakerPrompts, 
  EnhancedRatingSystem, 
  ProfileAnalysisPanel 
} from './components/EnhancedSpeedDatingComponents';
import { Phase2Demo } from './components/Phase2Integration';
import ProfileSetupDemo from './components/ProfileSetupDemo';
import PhotoUpload from './components/PhotoUpload';
import TimerDemo from './components/TimerDemo';
import MatchingDiscoveryPage from './components/MatchingDiscoveryPage';
import SupportPage from './components/SupportPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import { TermsGate, ConsentCheckbox } from './components/TermsGate';
import CookiePolicy from './components/CookiePolicy';
import AIDateIdeaGenerator from './components/AIDateIdeaGenerator';
import SEOHead from './components/SEOHead';
import DetailedProfileQuestions from './components/DetailedProfileQuestions';
import RealSpeedDatingSession from './components/RealSpeedDatingSession';
import SimpleMatchSelection from './components/SimpleMatchSelection';
import VideoCall from './components/VideoCall';
import ProfessionalProfileSetup from './components/ProfessionalProfileSetup';
import ProfileView from './components/ProfileView';
import ProfileRouter from './components/ProfileRouter';
import AccountDashboard from './components/AccountDashboard';
import AdminDashboard from './components/AdminDashboard';
import AuthCallback from './components/AuthCallback';
import { DeleteAccountRequest, DeleteAccountConfirm } from './components/DeleteAccountPublic';
import CommunityGuidelines from './components/CommunityGuidelines';
import { 
  SkipToContent, 
  AccessibleButton, 
  AccessibleInput, 
  useFocusManagement 
} from './components/AccessibilityComponents';
import CoachingDashboard from './components/CoachingDashboard';
import PremiumDashboard from './components/PremiumDashboard';
import ProfileOptimization from './components/ProfileOptimization';
import CoachingModule from './components/CoachingModule';
import CoachingLesson from './components/CoachingLesson';
import ActionPlan from './components/ActionPlan';
import CompatibilityReports from './components/CompatibilityReports';
import AdvancedAnalytics from './components/AdvancedAnalytics';
import './components/ui/animations.css';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext();


// Session Warning Toast Component
const SessionWarningToast = ({ show, onStayLoggedIn }) => {
  if (!show) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 99999,
        maxWidth: '400px',
        animation: 'slideInRight 0.3s ease-out'
      }}
    >
      <div 
        style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          border: '2px solid #f59e0b',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 10px 40px rgba(245, 158, 11, 0.3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ fontSize: '24px' }}>⏰</div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#92400e' }}>
              Session Expiring Soon
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#78350f', lineHeight: '1.5' }}>
              You've been inactive for 28 minutes. Your session will expire in 2 minutes.
            </p>
            <button
              onClick={onStayLoggedIn}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
              }}
            >
              Stay Logged In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const activityTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);

  // Activity tracking - update last activity time on user interactions
  useEffect(() => {
    const updateActivity = () => {
      setLastActivity(Date.now());
      localStorage.setItem('lastActivity', Date.now().toString());
      // Dismiss warning if user becomes active
      if (showSessionWarning) {
        setShowSessionWarning(false);
      }
    };

    // Track various user activities
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Initialize last activity
    updateActivity();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, []);

  // Inactivity check - show warning at 28 min, logout at 30 min
  useEffect(() => {
    if (!token) return;

    const checkInactivity = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;
      const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes
      const WARNING_THRESHOLD = 28 * 60 * 1000; // 28 minutes

      if (timeSinceLastActivity > INACTIVITY_LIMIT) {
        console.log('⏰ 30 minutes of inactivity - logging out...');
        setShowSessionWarning(false);
        logout();
      } else if (timeSinceLastActivity > WARNING_THRESHOLD && !showSessionWarning) {
        // Show warning 2 minutes before timeout
        console.log('⚠️ Showing session expiration warning (2 minutes remaining)');
        setShowSessionWarning(true);
      }
    };

    // Check every minute
    activityTimerRef.current = setInterval(checkInactivity, 60 * 1000);

    return () => {
      if (activityTimerRef.current) {
        clearInterval(activityTimerRef.current);
      }
    };
  }, [token, lastActivity, showSessionWarning]);

  // Auto-refresh token every 20 minutes if user is active
  useEffect(() => {
    if (!token) return;

    const refreshToken = async () => {
      try {
        const now = Date.now();
        const timeSinceLastActivity = now - lastActivity;
        const ACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes

        // Only refresh if user was active in the last 5 minutes
        if (timeSinceLastActivity < ACTIVITY_THRESHOLD) {
          console.log('🔄 Auto-refreshing token (user is active)...');
          const response = await axios.post(`${API}/token/refresh`);
          const { access_token } = response.data;
          
          localStorage.setItem('token', access_token);
          setToken(access_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
          
          console.log('✅ Token refreshed successfully');
        } else {
          console.log('⏭️ Skipping token refresh (user inactive)');
        }
      } catch (error) {
        console.error('❌ Token refresh failed:', error);
        // If refresh fails, logout
        logout();
      }
    };

    // Refresh every 20 minutes
    refreshTimerRef.current = setInterval(refreshToken, 20 * 60 * 1000);

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [token, lastActivity]);

  // Listen for session expired event from API interceptor
  useEffect(() => {
    const handleSessionExpired = (event) => {
      console.log('📢 Session expired event received');
      const message = event.detail?.message || 'Your session has expired. Please log in again.';
      
      // Show alert
      alert(message);
      
      // Logout
      logout();
    };

    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // Without this guard, a stale/expired token in localStorage can race with the
    // AuthCallback flow — fetchUserInfo() receives 401, clears the token, and
    // wipes out the fresh Google token that AuthCallback just stored, leaving
    // the user stuck on the "Verifying with Google…" spinner.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (token) {
      // Set default axios header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token and get user info
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/me`);
      setUser(response.data);
    } catch (error) {
      // Token is invalid, remove it
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, rememberMe = false) => {
    const response = await axios.post(`${API}/login`, { username, password, remember_me: rememberMe });
    const { access_token, remember_me } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('rememberMe', remember_me ? 'true' : 'false');
    setToken(access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Reset activity tracking
    setLastActivity(Date.now());
    localStorage.setItem('lastActivity', Date.now().toString());
    
    await fetchUserInfo();
    return response.data;
  };

  const logout = (navigateFn) => {
    // Clear all timers
    if (activityTimerRef.current) {
      clearInterval(activityTimerRef.current);
    }
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    setToken(null);
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    
    // Use setTimeout to allow React state updates to propagate before navigation
    setTimeout(() => {
      if (navigateFn) {
        navigateFn('/login');
      } else {
        // Fallback to hard redirect if navigate not available
        window.location.href = '/login';
      }
    }, 0);
  };

  const register = async (userData) => {
    return await axios.post(`${API}/register`, userData);
  };

  const stayLoggedIn = () => {
    // Reset activity and dismiss warning
    setLastActivity(Date.now());
    localStorage.setItem('lastActivity', Date.now().toString());
    setShowSessionWarning(false);
    console.log('✅ User chose to stay logged in - activity reset');
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, setToken, login, logout, register, loading, stayLoggedIn, showSessionWarning }}>
      <SessionWarningToast show={showSessionWarning} onStayLoggedIn={stayLoggedIn} />
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, setUser, loading, logout } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-rose-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.terms_accepted_at) {
    return (
      <TermsGate
        onAccepted={(ts) => setUser({ ...user, terms_accepted_at: ts })}
        onLogout={() => logout()}
      />
    );
  }
  
  return children;
};

// Login Component
const Login = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [formData, setFormData] = useState({ username: '', password: '', rememberMe: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/profile');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(formData.username, formData.password, formData.rememberMe);
      // Navigation handled by useEffect when user state updates
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-container min-h-screen relative overflow-hidden">
      {/* Sophisticated Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88)), url('https://images.unsplash.com/photo-1671119525965-21bc08cfed60')`,
        }}
      ></div>
      
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6">
        <Card className="card-container w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20 shadow-2xl rounded-3xl">
          <CardHeader className="text-center pb-8">
            <img 
              src="/original_logo.png" 
              alt="Shot Clock Dating Logo" 
              className="h-48 w-48 mx-auto mb-8 object-contain transform hover:scale-105 transition-all duration-700"
              style={{ 
                imageRendering: 'crisp-edges',
                filter: 'contrast(1.1) saturate(1.1) brightness(1.1)',
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden'
              }}
            />
            <CardTitle className="text-3xl font-light text-white mb-2">Welcome Back</CardTitle>
            <p className="text-gray-300">Sign in to continue your dating journey</p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <AccessibleInput
                id="login-username"
                label="Username or Email"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                placeholder="Enter your username or email"
                required
                className="[&>input]:bg-white/10 [&>input]:backdrop-blur-md [&>input]:border-white/20 [&>input]:text-white [&>input]:placeholder-gray-400 [&>input]:pl-10 [&>input]:py-3 [&>input]:rounded-xl [&>input]:focus:border-rose-400 [&>input]:focus:ring-rose-400/50 [&>label]:text-gray-200"
                error={error && error.includes('username') ? error : null}
              />
              
              <div className="space-y-2">
                <label htmlFor="login-password" className="block text-sm font-medium text-gray-200">
                  Password <span className="text-red-500 ml-1" aria-label=" (required)">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" aria-hidden="true" />
                  <Input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 pl-10 pr-10 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                    placeholder="Enter your password"
                    required
                    aria-required="true"
                    aria-invalid={error && error.includes('password') ? 'true' : 'false'}
                    aria-describedby={error && error.includes('password') ? 'password-error' : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-rose-400 rounded-full p-1"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Remember Me Checkbox */}
              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={(e) => setFormData({...formData, rememberMe: e.target.checked})}
                  className="h-4 w-4 rounded border-white/20 bg-white/10 text-rose-500 focus:ring-rose-400 focus:ring-offset-0"
                />
                <label htmlFor="remember-me" className="ml-2 text-sm text-gray-300 cursor-pointer">
                  Remember me for 7 days
                </label>
              </div>

              {error && (
                <div 
                  className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-red-300 text-sm"
                  role="alert"
                  aria-live="polite"
                  id="login-error"
                >
                  <span className="sr-only">Error: </span>
                  {error}
                  {error.toLowerCase().includes('confirm') && (
                    <div className="mt-3 pt-3 border-t border-red-500/20">
                      <p className="text-gray-300 text-xs mb-2">Need a new confirmation email?</p>
                      <Button
                        type="button"
                        onClick={() => navigate('/resend-confirmation')}
                        variant="outline"
                        className="text-xs py-1 px-3 bg-red-500/20 border-red-500/30 text-red-200 hover:bg-red-500/30 hover:text-white"
                      >
                        Resend Confirmation Email
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <AccessibleButton
                type="submit"
                loading={loading}
                disabled={loading}
                className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white py-3 px-4 rounded-xl font-semibold transition-all transform hover:scale-[1.02] focus:ring-rose-300"
                ariaLabel="Sign in to your account"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </AccessibleButton>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full bg-white/20" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-transparent px-2 text-gray-400">Or continue with</span>
              </div>
            </div>

            {/* Google Sign-In Button */}
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <Button
              type="button"
              onClick={() => {
                // Redirect to the dedicated /auth/callback route — NOT /profile.
                // /profile is wrapped in ProtectedRoute which would bounce the user
                // to /login before the session_id in the URL hash can be exchanged.
                const redirectUrl = window.location.origin + '/auth/callback';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full bg-white hover:bg-gray-100 text-gray-800 py-3 px-4 rounded-xl font-semibold transition-all transform hover:scale-[1.02] flex items-center justify-center gap-3"
              data-testid="google-signin-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="text-center space-y-4 mt-6">
              <AccessibleButton
                variant="ghost"
                onClick={() => navigate('/forgot-password')}
                className="text-rose-400 hover:text-rose-300 text-sm transition-colors focus:ring-rose-300"
                ariaLabel="Go to forgot password page"
              >
                Forgot your password?
              </AccessibleButton>
              
              <Separator className="bg-white/20" />
              
              <p className="text-gray-300 text-sm">
                Don't have an account?{' '}
                <AccessibleButton
                  variant="ghost"
                  onClick={() => navigate('/register')}
                  className="text-rose-400 hover:text-rose-300 font-semibold transition-colors focus:ring-rose-300 inline"
                  ariaLabel="Go to registration page"
                >
                  Sign up now
                </AccessibleButton>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Register Component
const Register = () => {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    age: '',
    bio: '',
    interests: '',
    gender: '',
    interested_in: [],
    location: '',
    latitude: null,
    longitude: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [legalAgreements, setLegalAgreements] = useState(false);

  console.log('📍 Register component rendering, success state:', success, 'loading:', loading, 'error:', error);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/profile');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    console.log('🚀 Registration form submitted');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Check legal policy agreements
    if (!legalAgreements) {
      setError("Please confirm you're 18 or older and agree to our Terms to create an account");
      setLoading(false);
      return;
    }

    try {
      // Validation for matching fields
      if (!formData.gender) {
        setError('Please select your gender');
        setLoading(false);
        return;
      }

      if (formData.interested_in.length === 0) {
        setError('Please select who you\'re interested in meeting');
        setLoading(false);
        return;
      }

      if (!formData.location) {
        setError('Please enter your location');
        setLoading(false);
        return;
      }

      // Validate bio length
      if (formData.bio.length < 10) {
        setError('Bio must be at least 10 characters long');
        setLoading(false);
        return;
      }

      const userData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        name: formData.name,
        age: parseInt(formData.age),
        bio: formData.bio,
        interests: formData.interests.split(',').map(i => i.trim()).filter(i => i),
        gender: formData.gender,
        interested_in: formData.interested_in,
        location: formData.location,
        latitude: formData.latitude,
        longitude: formData.longitude,
        accepted_terms: legalAgreements
      };

      console.log('📤 Sending registration request with data:', { ...userData, password: '***' });
      
      const response = await register(userData);
      console.log('✅ Registration response received:', response);
      
      console.log('🎉 Setting success state to true');
      setSuccess(true);
      console.log('✅ Success state set, should render success page now');
    } catch (error) {
      console.error('❌ Registration error:', error);
      console.error('Error response:', error.response);
      
      // Ensure we always set a string error message, never an object
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.response?.data?.detail) {
        // Backend error detail
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          errorMessage = error.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.log('Setting error message:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
      console.log('🏁 Registration process complete, loading:', false);
    }
  };

  if (success) {
    console.log('🎊 Rendering success page');
    return (
      <div className="login-page-container min-h-screen relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88)), url('https://images.unsplash.com/photo-1671119525965-21bc08cfed60')`,
          }}
        ></div>
        
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6">
          <Card className="card-container w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20 shadow-2xl rounded-3xl">
            <CardContent className="text-center py-12">
              <CheckCircle className="h-20 w-20 text-green-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">Registration Successful!</h2>
              <p className="text-gray-300 mb-6">
                Please check your email and click the confirmation link to activate your account.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 px-8 py-3 rounded-xl"
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page-container min-h-screen relative overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88)), url('https://images.unsplash.com/photo-1671119525965-21bc08cfed60')`,
        }}
      ></div>
      
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 py-8">
        <Card className="card-container w-full max-w-2xl backdrop-blur-xl bg-black/20 border border-white/20 shadow-2xl rounded-3xl">
          <CardHeader className="text-center">
            <img 
              src="/original_logo.png" 
              alt="Shot Clock Dating Logo" 
              className="h-48 w-48 mx-auto mb-8 object-contain transform hover:scale-105 transition-all duration-700"
              style={{ 
                imageRendering: 'crisp-edges',
                filter: 'contrast(1.1) saturate(1.1) brightness(1.1)',
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden'
              }}
            />
            <CardTitle className="text-3xl font-light text-white mb-2">Join Shot Clock Dating</CardTitle>
            <p className="text-gray-300">Create your account and start meeting amazing people</p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Google Sign-Up Button - Prominent at top */}
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <div className="space-y-4">
              <div className="bg-white/5 backdrop-blur-md border border-rose-400/30 rounded-xl p-4" data-testid="signup-consent-box">
                <ConsentCheckbox checked={legalAgreements} onChange={setLegalAgreements} />
                <p className="text-gray-400 text-xs mt-2 ml-7">Required for Google and email sign-up.</p>
              </div>
              <Button
                type="button"
                disabled={!legalAgreements}
                onClick={() => {
                  // Redirect to the dedicated /auth/callback route — NOT /profile/setup.
                  // AuthCallback inspects data.is_new_user and routes to /profile/setup
                  // (new user) or /profile (returning user) after exchanging session_id.
                  localStorage.setItem('scd_terms_accepted', 'true');
                  const redirectUrl = window.location.origin + '/auth/callback';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 py-3 px-4 rounded-xl font-semibold transition-all transform hover:scale-[1.02] flex items-center justify-center gap-3"
                data-testid="google-signup-top-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
              
              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full bg-white/20" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-transparent px-2 text-gray-400">Or create account with email</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">Username</label>
                  <Input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                    placeholder="Choose a username"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">Full Name</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                    placeholder="Your full name"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">Age</label>
                  <Input
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({...formData, age: e.target.value})}
                    className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                    placeholder="25"
                    min="18"
                    max="100"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">Password</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 pr-10 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                      placeholder="Create password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">Confirm Password</label>
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                    placeholder="Confirm password"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Bio <span className="text-xs text-gray-400">(minimum 10 characters)</span>
                </label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 rounded-xl resize-none focus:border-rose-400 focus:ring-rose-400/50"
                  placeholder="Tell us about yourself... (at least 10 characters)"
                  rows={3}
                  required
                  minLength={10}
                />
                {formData.bio && formData.bio.length < 10 && (
                  <p className="text-xs text-amber-400 mt-1">
                    {10 - formData.bio.length} more characters needed
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">Interests</label>
                <Input
                  type="text"
                  value={formData.interests}
                  onChange={(e) => setFormData({...formData, interests: e.target.value})}
                  className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                  placeholder="Travel, Music, Cooking, Photography (comma-separated)"
                />
              </div>

              {/* MATCHING INFORMATION - CRITICAL FOR DATING APP */}
              <div className="space-y-4 bg-gradient-to-r from-rose-500/10 to-pink-500/10 backdrop-blur-md border border-rose-400/20 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Heart className="h-5 w-5 text-rose-400 mr-2" />
                  Matching Preferences <span className="text-red-400 ml-1">*</span>
                </h3>
                <p className="text-gray-300 text-sm mb-4">
                  <span className="text-red-400">*</span> All fields in this section are required for matching
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Gender */}
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-2">
                      I am <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={formData.gender}
                      onChange={(e) => setFormData({...formData, gender: e.target.value})}
                      className="w-full bg-white/10 backdrop-blur-md border-white/20 text-white py-3 px-4 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                      required
                    >
                      <option value="" className="bg-gray-800">Select your gender</option>
                      <option value="male" className="bg-gray-800">Male</option>
                      <option value="female" className="bg-gray-800">Female</option>
                      <option value="non-binary" className="bg-gray-800">Non-binary</option>
                      <option value="other" className="bg-gray-800">Other</option>
                    </select>
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-2">
                      Location <span className="text-red-400">*</span>
                    </label>
                    <Input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                      placeholder="City, State (e.g., San Francisco, CA)"
                      required
                    />
                  </div>
                </div>

                {/* Interested In */}
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    I'm interested in <span className="text-red-400">*</span>
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['male', 'female', 'non-binary', 'other'].map((option) => (
                      <label key={option} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.interested_in.includes(option)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                interested_in: [...formData.interested_in, option]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                interested_in: formData.interested_in.filter(item => item !== option)
                              });
                            }
                          }}
                          className="h-4 w-4 text-rose-500 bg-white/10 border-white/20 rounded focus:ring-rose-400"
                        />
                        <span className="text-gray-300 capitalize">{option}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-gray-400 text-xs mt-2">
                    <span className="text-red-400">*</span> Required field - Select all that apply (you can change this later in your preferences)
                  </p>
                </div>
              </div>

              {/* Legal Policy Agreements (mirrors the checkbox at the top) */}
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6">
                <ConsentCheckbox checked={legalAgreements} onChange={setLegalAgreements} testId="terms-consent-checkbox-bottom" />
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-red-300 text-sm" data-testid="register-error">
                  {String(error)}
                </div>
              )}

              <EnhancedButton
                type="submit"
                disabled={loading}
                size="lg"
                className="w-full"
                loading={loading}
                icon={<Heart className="w-5 h-5" />}
              >
                Create Account
              </EnhancedButton>
            </form>

            <div className="text-center mt-6">
              <p className="text-gray-300 text-sm">
                Already have an account?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-rose-400 hover:text-rose-300 font-semibold transition-colors"
                >
                  Sign in here
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Homepage Component
const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Sophisticated Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.85), rgba(30, 41, 59, 0.75)), url('https://images.unsplash.com/photo-1671119525965-21bc08cfed60')`,
        }}
      ></div>
      
      {/* Elegant Overlay Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)`,
            backgroundSize: '50px 50px'
          }}
        ></div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float-particle ${5 + Math.random() * 5}s ease-in-out infinite`,
              animationDelay: `${i * 0.5}s`
            }}
          ></div>
        ))}
      </div>

      <div className="relative z-10 min-h-screen">
        {/* Hero Section */}
        <div className="flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-5xl mx-auto">
            {/* Logo Section */}
            <div className="flex justify-center items-center mb-12">
              <div className="relative group">
                <img 
                  src="/original_logo.png" 
                  alt="Shot Clock Dating Logo" 
                  className="h-56 w-56 object-contain transform group-hover:scale-105 transition-all duration-700"
                  style={{ 
                    imageRendering: 'crisp-edges',
                    filter: 'contrast(1.2) saturate(1.3) brightness(1.1)',
                    WebkitBackfaceVisibility: 'hidden',
                    backfaceVisibility: 'hidden'
                  }}
                />
              </div>
            </div>

            {/* Sophisticated Typography */}
            <div className="mb-12">
              <h1 className="text-7xl md:text-8xl font-light text-white mb-6 tracking-wide">
                Can You Beat The Shot Clock
                <span className="block font-semibold bg-gradient-to-r from-rose-400 via-pink-400 to-rose-500 bg-clip-text text-transparent">
                  to Find Your Match?
                </span>
              </h1>
              <div className="w-32 h-1 bg-gradient-to-r from-rose-400 to-pink-400 mx-auto mb-8 rounded-full opacity-80"></div>
              <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed font-light">
                Race against time to find your perfect match through curated speed dates. 
                <br className="hidden md:block" />
                <span className="text-rose-300 font-medium">Every conversation counts. Can you beat the clock?</span>
              </p>
            </div>

            {/* Sophisticated CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-8 mb-16">
              <Button 
                onClick={() => navigate('/profile')}
                className="group relative bg-gradient-to-r from-rose-500 via-pink-500 to-rose-600 hover:from-rose-600 hover:via-pink-600 hover:to-rose-700 text-white px-12 py-6 text-xl font-semibold rounded-full shadow-2xl hover:shadow-rose-500/25 transform hover:scale-105 transition-all duration-300 border-0 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <span className="relative flex items-center">
                  <img 
                    src="/original_logo.png" 
                    alt="Logo" 
                    className="h-12 w-12 mr-4 object-contain group-hover:scale-110 transition-transform duration-300"
                    style={{ 
                      imageRendering: 'crisp-edges',
                      filter: 'brightness(0) invert(1) contrast(1.2) saturate(1.1)',
                    }}
                  />
                  Begin Your Journey
                </span>
              </Button>
              
              <Button 
                onClick={() => navigate('/how-it-works')}
                variant="outline"
                className="group bg-white/10 backdrop-blur-md border-2 border-white/30 text-white hover:bg-white/20 hover:border-white/50 px-12 py-6 text-xl font-medium rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <span className="group-hover:text-rose-200 transition-colors duration-300">
                  Discover How
                </span>
              </Button>
            </div>
          </div>
        </div>

        {/* Sophisticated Features Section */}
        <div className="bg-gradient-to-b from-transparent via-black/20 to-black/40 backdrop-blur-sm py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-5xl font-light text-white mb-6">
                Crafted for 
                <span className="font-semibold bg-gradient-to-r from-rose-400 to-pink-400 bg-clip-text text-transparent"> Connection</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-rose-400 to-pink-400 mx-auto rounded-full"></div>
            </div>

            <div className="grid md:grid-cols-3 gap-12">
              {/* Feature 1 */}
              <div className="group text-center">
                <div className="relative mb-8">
                  <div className="w-24 h-24 bg-gradient-to-br from-rose-400/20 to-pink-400/20 backdrop-blur-md rounded-2xl mx-auto flex items-center justify-center group-hover:scale-110 transition-all duration-500 border border-white/10">
                    <Video className="h-12 w-12 text-rose-400 group-hover:text-rose-300 transition-colors duration-300" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-rose-400/10 to-pink-400/10 rounded-2xl blur-xl group-hover:blur-lg transition-all duration-500 -z-10"></div>
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-rose-200 transition-colors duration-300">
                  Premium Video Experience
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Crystal clear HD video calls designed for authentic, meaningful conversations that spark genuine connections.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="group text-center">
                <div className="relative mb-8">
                  <div className="w-24 h-24 bg-gradient-to-br from-rose-400/20 to-pink-400/20 backdrop-blur-md rounded-2xl mx-auto flex items-center justify-center group-hover:scale-110 transition-all duration-500 border border-white/10">
                    <Sparkles className="h-12 w-12 text-rose-400 group-hover:text-rose-300 transition-colors duration-300" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-rose-400/10 to-pink-400/10 rounded-2xl blur-xl group-hover:blur-lg transition-all duration-500 -z-10"></div>
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-rose-200 transition-colors duration-300">
                  Intelligent Conversation
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Thoughtfully curated conversation starters that break the ice naturally and reveal authentic personalities.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="group text-center">
                <div className="relative mb-8">
                  <div className="w-24 h-24 bg-gradient-to-br from-rose-400/20 to-pink-400/20 backdrop-blur-md rounded-2xl mx-auto flex items-center justify-center group-hover:scale-110 transition-all duration-500 border border-white/10">
                    <Users className="h-12 w-12 text-rose-400 group-hover:text-rose-300 transition-colors duration-300" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-rose-400/10 to-pink-400/10 rounded-2xl blur-xl group-hover:blur-lg transition-all duration-500 -z-10"></div>
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-rose-200 transition-colors duration-300">
                  Curated Connections
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Smart matching algorithm that connects you with compatible individuals who share your values and interests.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Homepage Ad for Free Users */}
        <div className="max-w-4xl mx-auto px-4 mb-16">
          <HomepageAd className="mx-auto max-w-2xl" />
        </div>

        {/* Elegant Footer Section */}
        <div className="bg-gradient-to-t from-black/60 to-transparent backdrop-blur-md py-16">
          <div className="max-w-4xl mx-auto text-center px-4">
            <h3 className="text-3xl font-light text-white mb-6">
              Ready to write your 
              <span className="font-semibold bg-gradient-to-r from-rose-400 to-pink-400 bg-clip-text text-transparent"> love story</span>?
            </h3>
            <p className="text-gray-300 text-lg mb-8 font-light">
              Join a community where every conversation has the potential to change your life.
            </p>
            
            {/* Share Button */}
            <div className="flex justify-center mb-8">
              <ShareButton className="text-white" />
            </div>
            
            <Button 
              onClick={() => navigate('/profile')}
              className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white px-10 py-4 text-lg font-medium rounded-full shadow-xl hover:shadow-rose-500/25 transform hover:scale-105 transition-all duration-300 mb-6"
            >
              Start Your Story Today
            </Button>

            <Button 
              onClick={() => navigate('/subscription/plans')}
              variant="outline"
              className="border-rose-400 text-rose-400 hover:bg-rose-400 hover:text-white px-8 py-3 text-base font-medium rounded-full shadow-lg hover:shadow-rose-400/25 transform hover:scale-105 transition-all duration-300"
            >
              ✨ Upgrade to Premium
            </Button>

            {/* PWA Settings Link */}
            <div className="mt-8 pt-6 border-t border-white/10">
              <button
                onClick={() => navigate('/pwa-settings')}
                className="text-gray-400 hover:text-rose-400 text-sm transition-colors flex items-center mx-auto"
              >
                <span>App Settings</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// How It Works Component
const HowItWorks = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-rose-400/20 to-pink-400/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="flex justify-center items-center mb-8">
              <img 
                src="/original_logo.png" 
                alt="Shot Clock Dating Logo" 
                className="h-56 w-56 object-contain"
                style={{ 
                  imageRendering: 'crisp-edges',
                  filter: 'contrast(1.1) saturate(1.2)',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden'
                }}
              />
            </div>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Master the art of virtual speed dating with our step-by-step guide!
            </p>
          </div>
        </div>
      </div>

      {/* Steps Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="flex items-center mb-6">
              <div className="bg-rose-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mr-4">
                1
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Set Up Your Profile</h2>
            </div>
            <p className="text-gray-600 text-lg mb-4">
              Create your dating profile with your name, interests, and what makes you unique. 
              This is your chance to make a great first impression before your first conversation!
            </p>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-center"><Clock className="h-4 w-4 text-rose-500 mr-2" />Add your bio and interests</li>
              <li className="flex items-center"><Clock className="h-4 w-4 text-rose-500 mr-2" />Share what makes you unique</li>
              <li className="flex items-center"><Clock className="h-4 w-4 text-rose-500 mr-2" />Get ready to connect!</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-rose-100 to-pink-100 rounded-lg p-8 text-center">
            <Users className="h-24 w-24 text-rose-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Your Profile is Your Introduction</h3>
            <p className="text-gray-600">Make it count - first impressions matter!</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="bg-gradient-to-br from-rose-100 to-pink-100 rounded-lg p-8 text-center lg:order-1">
            <Video className="h-24 w-24 text-rose-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">HD Video Connections</h3>
            <p className="text-gray-600">Face-to-face conversations that feel natural and authentic</p>
          </div>
          <div className="lg:order-2">
            <div className="flex items-center mb-6">
              <div className="bg-rose-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mr-4">
                2
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Go on a Speed Date</h2>
            </div>
            <p className="text-gray-600 text-lg mb-4">
              When you're ready to connect, start your speed date and get matched with other singles. 
              Each conversation is exactly 5 minutes - enough time to spark a connection!
            </p>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-center"><Clock className="h-4 w-4 text-rose-500 mr-2" />5-minute video conversations</li>
              <li className="flex items-center"><Clock className="h-4 w-4 text-rose-500 mr-2" />HD video quality</li>
              <li className="flex items-center"><Clock className="h-4 w-4 text-rose-500 mr-2" />Safe, moderated environment</li>
            </ul>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="flex items-center mb-6">
              <div className="bg-rose-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mr-4">
                3
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Break the Ice</h2>
            </div>
            <p className="text-gray-600 text-lg mb-4">
              Never run out of things to talk about! Our AI-powered icebreaker questions appear during 
              your conversation to keep things flowing naturally.
            </p>
            <div className="bg-rose-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-rose-700 mb-2">Sample Icebreakers:</h4>
              <ul className="space-y-1 text-sm text-rose-600">
                <li>"If you could teleport anywhere right now, where would you go?"</li>
                <li>"What's something you've learned about yourself recently?"</li>
                <li>"What's a hobby you've always wanted to try?"</li>
              </ul>
            </div>
          </div>
          <div className="bg-gradient-to-br from-rose-100 to-pink-100 rounded-lg p-8 text-center">
            <Sparkles className="h-24 w-24 text-rose-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Smart Conversation Starters</h3>
            <p className="text-gray-600">5 categories, 15+ questions to keep conversations flowing</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="bg-gradient-to-br from-rose-100 to-pink-100 rounded-lg p-8 text-center lg:order-1">
            <Heart className="h-24 w-24 text-rose-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Make Your Move</h3>
            <p className="text-gray-600">Yes or Pass - it's that simple</p>
          </div>
          <div className="lg:order-2">
            <div className="flex items-center mb-6">
              <div className="bg-rose-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mr-4">
                4
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Make Your Decision</h2>
            </div>
            <p className="text-gray-600 text-lg mb-4">
              At the end of each 5-minute round, decide if you want to connect further. 
              Hit "Interested" if you felt a spark, or "Pass" to move on to the next match.
            </p>
            <div className="flex space-x-4">
              <div className="bg-gray-100 rounded-lg p-4 flex-1 text-center">
                <h4 className="font-semibold text-gray-700 mb-1">Pass</h4>
                <p className="text-sm text-gray-600">Move on to next match</p>
              </div>
              <div className="bg-rose-100 rounded-lg p-4 flex-1 text-center">
                <h4 className="font-semibold text-rose-700 mb-1">Interested</h4>
                <p className="text-sm text-rose-600">Show your interest!</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center mb-6">
              <div className="bg-rose-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mr-4">
                5
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Score Your Matches</h2>
            </div>
            <p className="text-gray-600 text-lg mb-4">
              When two players both choose "Interested," it's a MATCH! 🎉 
              You'll get instant notification and can continue your conversation in private chat.
            </p>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-center"><Clock className="h-4 w-4 text-rose-500 mr-2" />Instant match notifications</li>
              <li className="flex items-center"><Clock className="h-4 w-4 text-rose-500 mr-2" />Private chat with matches</li>
              <li className="flex items-center"><Clock className="h-4 w-4 text-rose-500 mr-2" />Plan your next date!</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-rose-100 to-pink-100 rounded-lg p-8 text-center">
            <MessageCircle className="h-24 w-24 text-rose-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">It's a Match!</h3>
            <p className="text-gray-600">Continue the conversation and see where it leads</p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-rose-500 to-pink-500 py-16">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Start Dating?</h2>
          <p className="text-xl text-rose-100 mb-8">
            Join thousands of singles who've found meaningful connections through Shot Clock Dating
          </p>
          <div className="flex justify-center space-x-4">
            <Button 
              onClick={() => navigate('/profile')}
              className="bg-white text-rose-500 hover:bg-gray-100 px-10 py-4 text-xl font-semibold"
            >
              <img 
                src="/original_logo.png" 
                alt="Logo" 
                className="h-10 w-10 mr-4 object-contain"
                style={{ 
                  imageRendering: 'crisp-edges',
                  filter: 'contrast(1.2) saturate(1.2)',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden'
                }}
              />
              Start Dating Now
            </Button>
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-rose-500 px-8 py-3 text-lg"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Profile Setup Component
const ProfileSetup = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // Get user data from auth context
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    age: '',
    bio: '',
    interests: ''
  });
  
  // Pre-populate form with user data when user is loaded
  useEffect(() => {
    if (user) {
      setFormData(prevData => ({
        ...prevData,
        name: user.name || '',
        email: user.email || '',
        age: user.age || '',
        bio: user.bio || '',
        interests: Array.isArray(user.interests) ? user.interests.join(', ') : (user.interests || '')
      }));
    }
  }, [user]);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [photos, setPhotos] = useState([]);

  // Timer effect - commented out since timer is removed
  // useEffect(() => {
  //   if (isTimerActive && timeRemaining > 0) {
  //     const timer = setInterval(() => {
  //       setTimeRemaining(prev => prev - 1);
  //     }, 1000);
  //     return () => clearInterval(timer);
  //   } else if (timeRemaining === 0) {
  //     setIsTimerActive(false);
  //   }
  // }, [timeRemaining, isTimerActive]);

  // Timer functions - commented out since timer is removed
  // const formatTime = (seconds) => {
  //   const mins = Math.floor(seconds / 60);
  //   const secs = seconds % 60;
  //   return `${mins}:${secs.toString().padStart(2, '0')}`;
  // };

  // const getTimerColor = () => {
  //   if (timeRemaining > 300) return 'text-emerald-400'; // Green
  //   if (timeRemaining > 120) return 'text-yellow-400';  // Yellow
  //   return 'text-rose-400'; // Red
  // };

  // const getTimerProgress = () => {
  //   return ((600 - timeRemaining) / 600) * 100;
  // };

  const fetchAISuggestions = async () => {
    setLoadingAI(true);
    try {
      // Create a temporary user profile for AI suggestions
      const tempProfile = {
        name: formData.name,
        age: parseInt(formData.age) || 25,
        bio: formData.bio || "Tell us about yourself...",
        interests: formData.interests.split(',').map(i => i.trim()).filter(i => i)
      };
      
      // If user is authenticated, use their profile, otherwise use temp profile
      const response = await axios.get(`${API}/profile/ai-suggestions`);
      setAiSuggestions(response.data.suggestions);
      setShowAISuggestions(true);
    } catch (error) {
      console.error('Error fetching AI suggestions:', error);
      // Show fallback suggestions
      setAiSuggestions({
        bio_suggestion: "Consider adding more specific details about your hobbies and what makes you unique!",
        interest_suggestions: ["Photography", "Cooking", "Hiking", "Reading", "Music"],
        profile_tips: "Be authentic and let your personality shine through your profile!"
      });
      setShowAISuggestions(true);
    } finally {
      setLoadingAI(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsAnimating(true);
    
    try {
      const userData = {
        ...formData,
        age: parseInt(formData.age),
        interests: formData.interests.split(',').map(i => i.trim()).filter(i => i)
      };
      
      const response = await axios.post(`${API}/users`, userData);
      localStorage.setItem('userId', response.data.id);
      localStorage.setItem('userName', response.data.name);
      
      // Add exciting animation delay
      setTimeout(() => {
        navigate('/speed-dating');
      }, 1500);
    } catch (error) {
      console.error('Error creating profile:', error);
      setIsAnimating(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Sophisticated Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88)), url('https://images.unsplash.com/photo-1671119525965-21bc08cfed60')`,
        }}
      ></div>
      
      {/* Elegant Overlay Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(244,63,94,0.4) 1px, transparent 0)`,
            backgroundSize: '60px 60px'
          }}
        ></div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute w-3 h-3 bg-rose-400/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float-elegant ${4 + Math.random() * 3}s ease-in-out infinite`,
              animationDelay: `${i * 0.8}s`
            }}
          ></div>
        ))}
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <div className="max-w-4xl w-full">

          <Card className="backdrop-blur-xl bg-black/20 border border-white/20 shadow-2xl rounded-3xl overflow-hidden">
            {/* Sophisticated Header */}
            <div className="bg-gradient-to-r from-rose-500/80 via-pink-500/80 to-rose-600/80 backdrop-blur-md p-8 text-center border-b border-white/10">
              <div className="flex justify-center mb-6">
                <div className="relative group">
                  <img 
                    src="/original_logo.png" 
                    alt="Shot Clock Dating Logo" 
                    className="h-56 w-56 object-contain group-hover:scale-105 transition-all duration-700"
                    style={{ 
                      imageRendering: 'crisp-edges',
                      filter: 'contrast(1.1) saturate(1.1) brightness(1.1)',
                      WebkitBackfaceVisibility: 'hidden',
                      backfaceVisibility: 'hidden'
                    }}
                    onError={(e) => {
                      console.error('Logo failed to load:', e.target.src);
                      // Fallback to a different logo if original fails
                      if (e.target.src.includes('original_logo.png')) {
                        e.target.src = '/shot-clock-logo.png';
                      }
                    }}
                    onLoad={() => {
                      console.log('Logo loaded successfully');
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/20 rounded-full blur-3xl -z-10 group-hover:blur-2xl transition-all duration-700"></div>
                </div>
              </div>
              
              <h1 className="text-4xl font-light text-white mb-3">
                Craft Your 
                <span className="font-semibold"> Perfect Profile</span>
              </h1>
              <p className="text-rose-100 text-lg font-light mb-6">
                Step {currentStep} of 4 - Your love story begins with authenticity
              </p>
              
              {/* Elegant Progress Bar */}
              <div className="relative bg-white/10 rounded-full h-2 overflow-hidden">
                <div 
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-white via-rose-200 to-white rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${(currentStep / 4) * 100}%` }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
              </div>
            </div>

            <CardContent className="p-10">
              {/* Profile Creation Ad for Free Users */}
              <div className="mb-8">
                <ProfileCreationAd />
              </div>

              <form onSubmit={handleSubmit}>
                {/* Step 1: Basic Info */}
                {currentStep === 1 && (
                  <div className="space-y-8 animate-fadeIn">
                    <div className="text-center mb-8">
                      <div className="text-7xl mb-6">✨</div>
                      <h2 className="text-3xl font-light text-white mb-3">Welcome to Your Journey</h2>
                      <p className="text-gray-300 text-lg">Let's start with the essentials</p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-3">
                          Your Name
                          <span className="text-blue-400 text-xs ml-2">(can be updated)</span>
                        </label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Enter your full name"
                          className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 text-lg py-4 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-3">
                          Age
                        </label>
                        <Input
                          type="number"
                          value={formData.age}
                          onChange={(e) => setFormData({...formData, age: e.target.value})}
                          placeholder="25"
                          min="18"
                          max="100"
                          className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 text-lg py-4 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-3">
                        Email Address
                        <span className="text-green-400 text-xs ml-2">(from registration)</span>
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        readOnly
                        placeholder="your@email.com"
                        className="bg-white/5 backdrop-blur-md border-white/10 text-gray-300 placeholder-gray-500 text-lg py-4 rounded-xl cursor-not-allowed"
                        title="Email address from your registration - cannot be changed here"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        This is the email you used during registration. To change it, please contact support.
                      </p>
                    </div>
                  </div>
                )}

                {/* Step 2: About You */}
                {currentStep === 2 && (
                  <div className="space-y-8 animate-fadeIn">
                    <div className="text-center mb-8">
                      <div className="text-7xl mb-6">💫</div>
                      <h2 className="text-3xl font-light text-white mb-3">Share Your Story</h2>
                      <p className="text-gray-300 text-lg">What makes you uniquely you?</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-3">
                        Your Bio
                      </label>
                      <Textarea
                        value={formData.bio}
                        onChange={(e) => setFormData({...formData, bio: e.target.value})}
                        placeholder="Share your passions, dreams, and what you're looking for in a meaningful connection..."
                        rows={6}
                        className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 text-lg rounded-xl resize-none focus:border-rose-400 focus:ring-rose-400/50"
                        required
                      />
                      <p className="text-gray-400 text-sm mt-3 font-light">
                        💡 Be authentic - genuine connections start with honest stories
                      </p>
                    </div>

                    {/* AI Suggestions Button */}
                    <div className="text-center">
                      <Button
                        type="button"
                        onClick={fetchAISuggestions}
                        disabled={loadingAI || !formData.name || !formData.age}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl text-sm font-medium shadow-lg hover:shadow-purple-500/25 transform hover:scale-105 transition-all duration-300"
                      >
                        {loadingAI ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                            Getting AI Suggestions...
                          </div>
                        ) : (
                          <>🤖 Get AI Profile Suggestions</>
                        )}
                      </Button>
                    </div>

                    {/* AI Suggestions Display */}
                    {showAISuggestions && aiSuggestions && (
                      <div className="bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-purple-500/10 backdrop-blur-md p-6 rounded-xl border border-purple-500/20 animate-fadeIn">
                        <div className="flex items-center mb-4">
                          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-3">
                            <span className="text-white text-sm font-bold">AI</span>
                          </div>
                          <h3 className="text-lg font-semibold text-white">✨ AI Profile Suggestions</h3>
                        </div>
                        
                        {/* Bio Suggestion */}
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-purple-200 mb-2">💬 Suggested Bio Improvement:</h4>
                          <p className="text-gray-200 text-sm bg-white/5 p-3 rounded-lg">
                            {aiSuggestions.bio_suggestion}
                          </p>
                        </div>

                        {/* Interest Suggestions */}
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-purple-200 mb-2">🎯 Suggested Interests:</h4>
                          <div className="flex flex-wrap gap-2">
                            {aiSuggestions.interest_suggestions?.map((interest, index) => (
                              <Badge 
                                key={index} 
                                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 text-xs rounded-full cursor-pointer hover:scale-105 transition-transform"
                                onClick={() => {
                                  const currentInterests = formData.interests ? formData.interests.split(',').map(i => i.trim()) : [];
                                  if (!currentInterests.includes(interest)) {
                                    const newInterests = [...currentInterests, interest].join(', ');
                                    setFormData({...formData, interests: newInterests});
                                  }
                                }}
                              >
                                + {interest}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {/* Profile Tips */}
                        <div>
                          <h4 className="text-sm font-medium text-purple-200 mb-2">💎 Profile Tips:</h4>
                          <p className="text-gray-200 text-sm bg-white/5 p-3 rounded-lg">
                            {aiSuggestions.profile_tips}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3: Photo Upload */}
                {currentStep === 3 && (
                  <div className="space-y-8 animate-fadeIn">
                    <div className="text-center mb-8">
                      <div className="text-7xl mb-6">📸</div>
                      <h2 className="text-3xl font-light text-white mb-3">Show Your Best Self</h2>
                      <p className="text-gray-300 text-lg">Upload photos that capture your personality</p>
                    </div>
                    
                    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                      <PhotoUpload 
                        onPhotosChange={setPhotos}
                        existingPhotos={photos}
                      />
                    </div>
                  </div>
                )}

                {/* Step 4: Interests */}
                {currentStep === 4 && (
                  <div className="space-y-8 animate-fadeIn">
                    <div className="text-center mb-8">
                      <div className="text-7xl mb-6">🎯</div>
                      <h2 className="text-3xl font-light text-white mb-3">Your Passions</h2>
                      <p className="text-gray-300 text-lg">Connect through shared interests</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-3">
                        Interests & Hobbies
                      </label>
                      <Input
                        value={formData.interests}
                        onChange={(e) => setFormData({...formData, interests: e.target.value})}
                        placeholder="Travel, Music, Cooking, Art, Photography, Adventure..."
                        className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 text-lg py-4 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                      />
                      <p className="text-gray-400 text-sm mt-3 font-light">
                        ✨ Separate with commas - shared interests spark the best conversations
                      </p>
                    </div>

                    {/* Sophisticated Interest Preview */}
                    {formData.interests && (
                      <div className="bg-gradient-to-r from-rose-500/10 via-pink-500/10 to-rose-500/10 backdrop-blur-md p-6 rounded-xl border border-white/10">
                        <p className="text-sm font-medium text-gray-200 mb-4">Interest Preview:</p>
                        <div className="flex flex-wrap gap-3">
                          {formData.interests.split(',').map((interest, index) => (
                            <Badge key={index} className="bg-gradient-to-r from-rose-500 to-pink-500 text-white px-4 py-2 text-sm font-medium rounded-full border-0">
                              {interest.trim()}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Sophisticated Navigation */}
                <div className="flex justify-between items-center mt-12 pt-8 border-t border-white/10">
                  {currentStep > 1 ? (
                    <Button 
                      type="button"
                      onClick={prevStep}
                      className="bg-white/10 backdrop-blur-md border border-white/30 text-white hover:bg-white/20 px-8 py-4 text-lg rounded-full"
                    >
                      ← Previous
                    </Button>
                  ) : (
                    <div></div>
                  )}

                  {currentStep < 4 ? (
                    <Button 
                      type="button"
                      onClick={nextStep}
                      disabled={
                        (currentStep === 1 && (!formData.name || !formData.email || !formData.age)) ||
                        (currentStep === 2 && !formData.bio)
                      }
                      className="group relative bg-gradient-to-r from-rose-500 via-pink-500 to-rose-600 hover:from-rose-600 hover:via-pink-600 hover:to-rose-700 text-white px-10 py-4 text-lg font-semibold rounded-full shadow-2xl hover:shadow-rose-500/25 transform hover:scale-105 transition-all duration-300 border-0 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                      <span className="relative">Continue →</span>
                    </Button>
                  ) : (
                    <Button 
                      type="submit" 
                      disabled={isAnimating}
                      className="group relative bg-gradient-to-r from-rose-500 via-pink-500 to-rose-600 hover:from-rose-600 hover:via-pink-600 hover:to-rose-700 text-white px-12 py-4 text-xl font-bold rounded-full shadow-2xl hover:shadow-rose-500/25 transform hover:scale-105 transition-all duration-300 border-0 overflow-hidden"
                    >
                      {isAnimating ? (
                        <span className="flex items-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent mr-3"></div>
                          Entering Speed Dating...
                        </span>
                      ) : (
                        <span className="relative flex items-center">
                          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                          <img 
                            src="/original_logo.png" 
                            alt="Speed Dating Icon" 
                            className="h-6 w-6 mr-3 brightness-0 invert"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          Begin Speed Dating! 🚀
                        </span>
                      )}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Enhanced Speed Dating Component with Phase 1 Improvements
const SpeedDating = () => {
  const [currentRoom, setCurrentRoom] = useState(null);
  const [localTracks, setLocalTracks] = useState([]);
  const [remoteParticipants, setRemoteParticipants] = useState(new Map());
  // Timer removed - no longer needed
  // const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [icebreakers, setIcebreakers] = useState([]);
  const [currentIcebreaker, setCurrentIcebreaker] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [hasDecided, setHasDecided] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [icebreakerLoading, setIcebreakerLoading] = useState(true);
  const [profileSuggestions, setProfileSuggestions] = useState(null);
  const [matchInsights, setMatchInsights] = useState(null);
  const [conversationTips, setConversationTips] = useState([]);
  const [sessionStats, setSessionStats] = useState({
    matches: 0,
    conversations: 0,
    totalTime: 0
  });
  
  // Demo mode states
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [currentDemoMatch, setCurrentDemoMatch] = useState(0);
  const [demoMatches] = useState([
    {
      id: 1,
      name: "Sarah Chen",
      age: 28,
      photo: "https://images.unsplash.com/photo-1569913486515-b74bf7751574?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxhc2lhbiUyMHdvbWFuJTIwaGVhZHNob3R8ZW58MHx8fHwxNzU0MTMwODM1fDA&ixlib=rb-4.1.0&q=85",
      interests: ["Photography", "Travel", "Coffee"],
      bio: "Love exploring new places and capturing memories!"
    },
    {
      id: 2,
      name: "Emma Rodriguez",
      age: 26,
      photo: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxmZW1hbGUlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NTQxMjk4MjJ8MA&ixlib=rb-4.1.0&q=85",
      interests: ["Fitness", "Cooking", "Dancing"],
      bio: "Fitness enthusiast who loves cooking healthy meals"
    },
    {
      id: 3,
      name: "Alex Johnson",
      age: 30,
      photo: "https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg",
      interests: ["Music", "Reading", "Hiking"],
      bio: "Musician and bookworm seeking adventure partner"
    }
  ]);
  
  const localVideoRef = useRef();
  const remoteVideoRef = useRef();
  
  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName');

  // Timer useEffect removed - no longer needed since timer was removed from UI
  // useEffect(() => {
  //   if (timeLeft > 0) {
  //     const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
  //     return () => clearTimeout(timer);
  //   } else {
  //     handleRoundEnd();
  //   }
  // }, [timeLeft]);

  const fetchIcebreakers = async () => {
    try {
      console.log('🔄 Fetching icebreakers...');
      setIcebreakerLoading(true);
      
      // First try the authenticated endpoint
      const response = await axios.get(`${API}/icebreakers`);
      console.log('✅ Authenticated icebreakers fetched:', response.data.questions);
      setIcebreakers(response.data.questions);
      setAiGenerated(response.data.ai_generated || false);
      setIcebreakerLoading(false);
    } catch (error) {
      console.error('❌ Error fetching authenticated icebreakers:', error);
      // If authentication fails, try the demo endpoint
      try {
        console.log('🔄 Trying demo icebreakers...');
        const demoResponse = await axios.get(`${API}/icebreakers/demo`);
        console.log('✅ Demo icebreakers fetched:', demoResponse.data.questions);
        setIcebreakers(demoResponse.data.questions);
        setAiGenerated(demoResponse.data.ai_generated || false);
        console.log("📝 Using demo icebreakers for testing");
        setIcebreakerLoading(false);
      } catch (demoError) {
        console.error('❌ Error fetching demo icebreakers:', demoError);
        // Hardcoded fallback questions if all else fails
        console.log('🔄 Using hardcoded fallback icebreakers');
        const fallbackQuestions = [
          {"category": "Fun", "question": "If you could have dinner with any fictional character, who would it be?"},
          {"category": "Deep", "question": "What's something you've learned about yourself recently?"},
          {"category": "Travel", "question": "What's the most spontaneous trip you've ever taken?"}
        ];
        console.log('✅ Hardcoded icebreakers set:', fallbackQuestions);
        setIcebreakers(fallbackQuestions);
        setAiGenerated(false);
        setIcebreakerLoading(false);
      }
    }
  };

  // Enhanced AI-powered methods for Phase 1 improvements
  const fetchProfileSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/profile/ai-suggestions`);
      setProfileSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching profile suggestions:', error);
    }
  };

  const fetchMatchInsights = async (partnerId) => {
    try {
      const response = await axios.post(`${API}/compatibility/analyze`, {
        partner_id: partnerId || 'sample-partner'
      });
      setMatchInsights(response.data);
    } catch (error) {
      console.error('Error fetching match insights:', error);
    }
  };

  const getConversationCoaching = async (conversationContext) => {
    try {
      const response = await axios.post(`${API}/conversation-coaching`, {
        conversation_context: conversationContext || "Starting speed dating conversation"
      });
      setConversationTips(response.data.coaching_tips || []);
    } catch (error) {
      console.error('Error getting conversation coaching:', error);
    }
  };

  // Enhanced initialization
  useEffect(() => {
    fetchIcebreakers();
    fetchProfileSuggestions();
    getConversationCoaching();
    
    return () => {
      if (currentRoom) {
        currentRoom.disconnect();
      }
    };
  }, []);

  // Update session stats
  const updateSessionStats = (statType, increment = 1) => {
    setSessionStats(prev => ({
      ...prev,
      [statType]: prev[statType] + increment
    }));
  };

  const startSpeedDating = async () => {
    try {
      // Check if there are other users available
      const usersResponse = await axios.get(`${API}/users/active`).catch(() => ({ data: { count: 1 } }));
      
      if (usersResponse.data.count <= 1) {
        // No other users available, start demo mode
        console.log("Starting demo mode - no other users available");
        setIsDemoMode(true);
        setIsConnected(true);
        setCurrentDemoMatch(0);
        
        // Fetch demo icebreakers
        await fetchIcebreakers();
        
        return;
      }

      // Get video token
      const roomName = `speed-dating-${Date.now()}`;
      const tokenResponse = await axios.post(`${API}/video/token`, {
        user_id: userId,
        room_name: roomName
      });

      // Create local tracks
      const tracks = await createLocalTracks({
        audio: true,
        video: { width: 640, height: 480 }
      });
      setLocalTracks(tracks);

      // Connect to room
      const room = await connect(tokenResponse.data.token, {
        name: roomName,
        tracks: tracks
      });

      setCurrentRoom(room);
      setIsConnected(true);

      // Attach local video
      const videoTrack = tracks.find(track => track.kind === 'video');
      if (videoTrack && localVideoRef.current) {
        videoTrack.attach(localVideoRef.current);
      }

      // Handle participants
      room.participants.forEach(participant => {
        handleParticipantConnected(participant);
      });

      room.on('participantConnected', handleParticipantConnected);
      room.on('participantDisconnected', handleParticipantDisconnected);

    } catch (error) {
      console.error('Error starting speed dating:', error);
      // Fallback to demo mode if there's an error
      console.log("Falling back to demo mode due to error");
      setIsDemoMode(true);
      setIsConnected(true);
      setCurrentDemoMatch(0);
      await fetchIcebreakers();
    }
  };

  const handleParticipantConnected = (participant) => {
    setRemoteParticipants(prev => new Map(prev.set(participant.sid, participant)));

    // Handle existing tracks
    participant.tracks.forEach(publication => {
      if (publication.isSubscribed) {
        handleTrackSubscribed(publication.track);
      }
    });

    // Handle track events
    participant.on('trackSubscribed', handleTrackSubscribed);
    participant.on('trackUnsubscribed', handleTrackUnsubscribed);
  };

  const handleParticipantDisconnected = (participant) => {
    setRemoteParticipants(prev => {
      const newMap = new Map(prev);
      newMap.delete(participant.sid);
      return newMap;
    });
  };

  const handleTrackSubscribed = (track) => {
    if (track.kind === 'video' && remoteVideoRef.current) {
      track.attach(remoteVideoRef.current);
    }
  };

  const handleTrackUnsubscribed = (track) => {
    track.detach();
  };

  const makeDecision = async (decision, metadata = {}) => {
    if (hasDecided) return;
    
    try {
      await axios.post(`${API}/match-decision`, {
        user_id: userId,
        target_user_id: 'partner-id', // This would come from the current partner
        decision: decision,
        session_id: currentRoom?.name || 'test-session',
        mood: metadata.mood,
        conversation_quality: metadata.conversationQuality || 'good'
      });
      
      setHasDecided(true);
      
      // Update session stats
      if (decision) {
        updateSessionStats('matches');
      }
      updateSessionStats('conversations');
      
      if (isDemoMode) {
        // In demo mode, move to next demo match after decision
        setTimeout(() => {
          const nextMatch = (currentDemoMatch + 1) % demoMatches.length;
          setCurrentDemoMatch(nextMatch);
          setHasDecided(false);
          setCurrentIcebreaker(0);
        }, 2000);
      }
      
    } catch (error) {
      console.error('Error submitting decision:', error);
    }
  };

  const handleRoundEnd = () => {
    // Timer removed - skip time calculation
    // updateSessionStats('totalTime', 300 - timeLeft);
    
    // Disconnect and prepare for next round
    if (currentRoom) {
      currentRoom.disconnect();
    }
    setIsConnected(false);
    setHasDecided(false);
    // setTimeLeft(300); // Timer removed
    setCurrentIcebreaker((prev) => (prev + 1) % icebreakers.length);
    
    // Fetch new coaching for next round
    getConversationCoaching("New speed dating round starting");
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4 border-rose-200 shadow-xl">
          <CardHeader className="text-center">
            <img 
              src="/original_logo.png" 
              alt="Shot Clock Dating Logo" 
              className="h-56 w-56 mx-auto mb-8 object-contain"
              style={{ 
                imageRendering: 'crisp-edges',
                filter: 'contrast(1.1) saturate(1.2)',
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden'
              }}
            />
            <CardTitle className="text-2xl text-rose-600">Ready to Meet Someone Special?</CardTitle>
            <p className="text-gray-600">Your speed date is about to begin - click below to start!</p>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={startSpeedDating}
              className="w-full bg-rose-500 hover:bg-rose-600 text-xl py-4 font-semibold"
            >
              <img 
                src="/original_logo.png" 
                alt="Logo" 
                className="h-10 w-10 mr-4 brightness-0 invert object-contain"
                style={{ 
                  imageRendering: 'crisp-edges',
                  filter: 'brightness(0) invert(1) contrast(1.2)',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden'
                }}
              />
              Start My Session
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 p-4 relative">
      {/* Profile Analysis Panel */}
      <ProfileAnalysisPanel 
        userProfile={{ userId, userName }}
        suggestions={profileSuggestions}
      />
      
      <div className="max-w-6xl mx-auto">
        {/* Timer Removed for Cleaner Interface */}

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          {/* Video Section */}
          <div className="lg:col-span-2">
            <Card className="border-rose-200 h-full shadow-lg">
              <CardContent className="p-6">
                <div className="grid md:grid-cols-2 gap-4 h-96">
                  {/* Local Video */}
                  <div className="relative bg-gray-900 rounded-lg overflow-hidden">
                    <video
                      ref={localVideoRef}
                      autoPlay
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
                      You ({userName})
                    </div>
                  </div>
                  
                  {/* Remote Video */}
                  <div className="relative bg-gray-900 rounded-lg overflow-hidden">
                    {isDemoMode ? (
                      // Demo mode - show demo user photo
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-rose-500 to-pink-500">
                        <img
                          src={demoMatches[currentDemoMatch]?.photo}
                          alt={demoMatches[currentDemoMatch]?.name}
                          className="w-24 h-24 rounded-full object-cover border-4 border-white/50 mb-4"
                        />
                        <div className="text-white text-center">
                          <h3 className="text-xl font-semibold">{demoMatches[currentDemoMatch]?.name}</h3>
                          <p className="text-white/80">{demoMatches[currentDemoMatch]?.age} years old</p>
                          <div className="mt-2 flex flex-wrap gap-1 justify-center">
                            {demoMatches[currentDemoMatch]?.interests.map((interest, idx) => (
                              <span key={idx} className="px-2 py-1 bg-white/20 rounded-full text-xs">
                                {interest}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Real video mode
                      <video
                        ref={remoteVideoRef}
                        autoPlay
                        playsInline
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
                      {isDemoMode ? demoMatches[currentDemoMatch]?.name : 'Your Match'}
                      {isDemoMode && <span className="ml-2 text-xs bg-blue-500 px-2 py-0.5 rounded">DEMO</span>}
                    </div>
                  </div>
                </div>
                
                {/* Enhanced Rating System */}
                <div className="mt-6">
                  <EnhancedRatingSystem 
                    onDecision={makeDecision}
                    hasDecided={hasDecided}
                    showMoodIndicators={true}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Enhanced Icebreaker Questions */}
          <div>
            <AIIcebreakerPrompts
              icebreakers={icebreakers}
              currentIcebreaker={currentIcebreaker}
              aiGenerated={aiGenerated}
              onNewPrompts={fetchIcebreakers}
              loading={icebreakerLoading}
            />
          </div>
        </div>

        {/* Session Statistics - Optional Display */}
        <Card className="mt-6 border-rose-200 bg-white/50 backdrop-blur-sm">
          <CardContent className="py-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-rose-600">{sessionStats.matches}</div>
                <div className="text-sm text-gray-600">Matches</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">{sessionStats.conversations}</div>
                <div className="text-sm text-gray-600">Conversations</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{Math.floor(sessionStats.totalTime / 60)}</div>
                <div className="text-sm text-gray-600">Minutes Dating</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Speed Dating Ad for Free Users */}
        <div className="mt-6">
          <SpeedDatingBreakAd />
        </div>
      </div>
    </div>
  );
};

// Forgot Password Component
const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/forgot-password`, { email });
      setSuccess(true);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88)), url('https://images.unsplash.com/photo-1671119525965-21bc08cfed60')`,
          }}
        ></div>
        
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <Card className="w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20 shadow-2xl rounded-3xl">
            <CardContent className="text-center py-12">
              <Mail className="h-20 w-20 text-blue-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">Check Your Email</h2>
              <p className="text-gray-300 mb-6">
                If your email is registered, you'll receive a password reset link shortly.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 px-8 py-3 rounded-xl"
              >
                Back to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88)), url('https://images.unsplash.com/photo-1671119525965-21bc08cfed60')`,
        }}
      ></div>
      
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <Card className="w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20 shadow-2xl rounded-3xl">
          <CardHeader className="text-center">
            <img 
              src="/original_logo.png" 
              alt="Shot Clock Dating Logo" 
              className="h-20 w-20 mx-auto mb-4 object-contain"
              style={{ 
                imageRendering: 'crisp-edges',
                filter: 'contrast(1.1) saturate(1.1) brightness(1.1)',
              }}
            />
            <CardTitle className="text-3xl font-light text-white mb-2">Forgot Password?</CardTitle>
            <p className="text-gray-300">Enter your email to receive a reset link</p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 pl-10 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-red-300 text-sm">
                  {String(error)}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-rose-500 via-pink-500 to-rose-600 hover:from-rose-600 hover:via-pink-600 hover:to-rose-700 text-white py-3 text-lg font-semibold rounded-xl shadow-2xl hover:shadow-rose-500/25 transform hover:scale-105 transition-all duration-300"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                    Sending Reset Link...
                  </div>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>

            <div className="text-center">
              <button
                onClick={() => navigate('/login')}
                className="text-rose-400 hover:text-rose-300 text-sm transition-colors"
              >
                Back to Login
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Password Reset Component
const PasswordReset = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    if (resetToken) {
      setToken(resetToken);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/reset-password`, {
        token,
        new_password: formData.password
      });
      setSuccess(true);
    } catch (error) {
      setError(error.response?.data?.detail || 'Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88)), url('https://images.unsplash.com/photo-1671119525965-21bc08cfed60')`,
          }}
        ></div>
        
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <Card className="w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20 shadow-2xl rounded-3xl">
            <CardContent className="text-center py-12">
              <CheckCircle className="h-20 w-20 text-green-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">Password Reset Successful!</h2>
              <p className="text-gray-300 mb-6">
                Your password has been reset successfully. You can now login with your new password.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 px-8 py-3 rounded-xl"
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88)), url('https://images.unsplash.com/photo-1671119525965-21bc08cfed60')`,
        }}
      ></div>
      
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <Card className="w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20 shadow-2xl rounded-3xl">
          <CardHeader className="text-center">
            <img 
              src="/original_logo.png" 
              alt="Shot Clock Dating Logo" 
              className="h-20 w-20 mx-auto mb-4 object-contain"
              style={{ 
                imageRendering: 'crisp-edges',
                filter: 'contrast(1.1) saturate(1.1) brightness(1.1)',
              }}
            />
            <CardTitle className="text-3xl font-light text-white mb-2">Reset Password</CardTitle>
            <p className="text-gray-300">Enter your new password</p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 pl-10 pr-10 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                    placeholder="Enter new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">Confirm New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 pl-10 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                    placeholder="Confirm new password"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-red-300 text-sm">
                  {String(error)}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-rose-500 via-pink-500 to-rose-600 hover:from-rose-600 hover:via-pink-600 hover:to-rose-700 text-white py-3 text-lg font-semibold rounded-xl shadow-2xl hover:shadow-rose-500/25 transform hover:scale-105 transition-all duration-300"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                    Resetting Password...
                  </div>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Email Confirmation Component
const EmailConfirmation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const confirmEmail = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (!token) {
        setError('Invalid confirmation link');
        setLoading(false);
        return;
      }

      try {
        console.log('Attempting email confirmation with token:', token.substring(0, 10) + '...');
        const response = await axios.post(`${API}/confirm-email`, { token });
        console.log('Confirmation response:', response.data);
        
        if (response.data.success !== false) {
          setSuccess(true);
        } else {
          setError(response.data.message || 'Email confirmation failed');
        }
      } catch (error) {
        console.error('Confirmation error:', error);
        console.error('Error response:', error.response?.data);
        
        const errorDetail = error.response?.data?.detail || error.response?.data?.message || 'Email confirmation failed';
        
        // Provide more user-friendly error messages for common scenarios
        if (errorDetail === 'Invalid confirmation token') {
          setError('This confirmation link has already been used or has expired. If you need a new confirmation email, please use the button below.');
        } else {
          setError(errorDetail);
        }
      } finally {
        setLoading(false);
      }
    };

    confirmEmail();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-rose-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg">Confirming your email...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-rose-900 flex items-center justify-center px-4">
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <Card className="w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20 shadow-2xl rounded-3xl">
          <CardContent className="text-center py-12">
            {success ? (
              <>
                <CheckCircle className="h-20 w-20 text-green-400 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-white mb-4">Email Confirmed!</h2>
                <p className="text-gray-300 mb-6">
                  Your email has been successfully confirmed. You can now log in and start dating!
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 px-8 py-3 rounded-xl"
                >
                  Go to Login
                </Button>
              </>
            ) : (
              <>
                <div className="h-20 w-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-red-400 text-4xl">✕</span>
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Confirmation Failed</h2>
                <p className="text-gray-300 mb-6">{error}</p>
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/resend-confirmation')}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 px-8 py-3 rounded-xl mb-3"
                  >
                    Resend Confirmation Email
                  </Button>
                  <Button
                    onClick={() => navigate('/register')}
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-xl"
                  >
                    Back to Register
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Resend Confirmation Component
const ResendConfirmation = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/resend-confirmation`, { email });
      setSuccess(true);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to resend confirmation email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-rose-900 flex items-center justify-center px-4">
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <Card className="w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20 shadow-2xl rounded-3xl">
          <CardContent className="py-12">
            <div className="text-center mb-8">
              <Mail className="h-16 w-16 text-rose-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Resend Confirmation</h2>
              <p className="text-gray-300">
                Enter your email address and we'll send you a new confirmation link
              </p>
            </div>

            {success ? (
              <div className="text-center">
                <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-4">Email Sent!</h3>
                <p className="text-gray-300 mb-6">
                  If your email is registered, you'll receive a new confirmation link shortly.
                </p>
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 px-8 py-3 rounded-xl"
                  >
                    Go to Login
                  </Button>
                  <Button
                    onClick={() => {
                      setSuccess(false);
                      setEmail('');
                    }}
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-xl"
                  >
                    Send Another
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="resend-email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address
                  </label>
                  <input
                    id="resend-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    placeholder="Enter your email address"
                    required
                  />
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4">
                    <p className="text-red-300 text-sm">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 disabled:opacity-50 px-8 py-3 rounded-xl"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Confirmation Email'
                  )}
                </Button>

                <div className="text-center pt-4">
                  <Button
                    type="button"
                    onClick={() => navigate('/login')}
                    variant="ghost"
                    className="text-gray-300 hover:text-white"
                  >
                    Back to Login
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main App Component
function App() {

// Setup Admin Component
const SetupAdmin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    setupPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/setup-admin`, {
        email: formData.email,
        setup_password: formData.setupPassword
      });

      console.log('✅ Admin setup successful:', response.data);
      setSuccess(true);
    } catch (error) {
      console.error('❌ Admin setup error:', error);
      
      let errorMessage = 'Admin setup failed. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88)), url('https://images.unsplash.com/photo-1671119525965-21bc08cfed60')`,
          }}
        ></div>
        
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <Card className="w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20 shadow-2xl rounded-3xl">
            <CardContent className="text-center py-12">
              <CheckCircle className="h-20 w-20 text-green-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">Admin Access Granted!</h2>
              <p className="text-gray-300 mb-6">
                You now have administrator privileges. You can access the admin dashboard.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 px-8 py-3 rounded-xl"
                >
                  Go to Login
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20 px-8 py-3 rounded-xl"
                >
                  Return to Homepage
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88)), url('https://images.unsplash.com/photo-1671119525965-21bc08cfed60')`,
        }}
      ></div>
      
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20 shadow-2xl rounded-3xl">
          <CardHeader className="text-center">
            <img 
              src="/original_logo.png" 
              alt="Shot Clock Dating Logo" 
              className="h-32 w-32 mx-auto mb-6 object-contain transform hover:scale-105 transition-all duration-700"
              style={{ 
                imageRendering: 'crisp-edges',
                filter: 'contrast(1.1) saturate(1.1) brightness(1.1)',
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden'
              }}
            />
            <CardTitle className="text-3xl font-light text-white mb-2">Setup Admin</CardTitle>
            <p className="text-gray-300 text-sm">One-time setup for first administrator</p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Information Box */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
              <h3 className="text-blue-300 font-semibold mb-2 flex items-center">
                <Lock className="h-4 w-4 mr-2" />
                Security Information
              </h3>
              <ul className="text-gray-300 text-sm space-y-1">
                <li>• This setup can only be used once</li>
                <li>• Requires special setup password</li>
                <li>• Enter your registered email</li>
              </ul>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Your Email <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 pl-10 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <p className="text-gray-400 text-xs mt-1">
                  Must be an email registered in the system
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Setup Password <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.setupPassword}
                    onChange={(e) => setFormData({...formData, setupPassword: e.target.value})}
                    className="bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-gray-400 pl-10 pr-10 py-3 rounded-xl focus:border-rose-400 focus:ring-rose-400/50"
                    placeholder="Enter setup password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <p className="text-gray-400 text-xs mt-1">
                  Contact support if you don't have the setup password
                </p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-red-300 text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white py-3 px-4 rounded-xl font-semibold transition-all transform hover:scale-[1.02]"
              >
                {loading ? 'Setting Up Admin...' : 'Setup Admin Access'}
              </Button>
            </form>

            <div className="text-center">
              <Separator className="bg-white/20 mb-4" />
              <p className="text-gray-300 text-sm">
                <button
                  onClick={() => navigate('/')}
                  className="text-rose-400 hover:text-rose-300 font-semibold transition-colors"
                >
                  Return to Homepage
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

  const [upgradeModal, setUpgradeModal] = useState({
    isOpen: false,
    feature: null,
    usageStats: null
  });

  // Service Worker Registration
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('PWA: Service worker registered successfully:', registration);
          
          // Check for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New content available, show update notification
                console.log('PWA: New content available, please refresh');
                // You can show a toast notification here
              }
            });
          });
        })
        .catch((error) => {
          console.error('PWA: Service worker registration failed:', error);
        });
    }
  }, []);

  // Handle upgrade modal events
  useEffect(() => {
    const handleUpgradeModal = (event) => {
      setUpgradeModal({
        isOpen: true,
        feature: event.detail.feature,
        usageStats: event.detail.usageStats
      });
    };

    window.addEventListener('showUpgradeModal', handleUpgradeModal);
    
    return () => {
      window.removeEventListener('showUpgradeModal', handleUpgradeModal);
    };
  }, []);

  // Fix icebreaker bubble duplication in fullscreen mode
  useEffect(() => {
    const fixIcebreakerDuplication = () => {
      // Check if we're in fullscreen mode
      const isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );

      // Add/remove CSS class to hide desktop icebreakers in fullscreen
      if (isFullscreen) {
        document.body.classList.add('fullscreen-active');
        
        // Hide any desktop icebreaker sections specifically
        const desktopIcebreakers = document.querySelectorAll('.hidden.md\\:block, [class*="space-y-6"][class*="hidden"]');
        desktopIcebreakers.forEach(element => {
          if (element.textContent && element.textContent.includes('Icebreaker')) {
            element.style.display = 'none';
          }
        });
      } else {
        document.body.classList.remove('fullscreen-active');
        
        // Restore desktop icebreakers when exiting fullscreen
        const desktopIcebreakers = document.querySelectorAll('.hidden.md\\:block, [class*="space-y-6"][class*="hidden"]');
        desktopIcebreakers.forEach(element => {
          if (element.textContent && element.textContent.includes('Icebreaker')) {
            element.style.display = '';
          }
        });
      }
    };

    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', fixIcebreakerDuplication);
    document.addEventListener('webkitfullscreenchange', fixIcebreakerDuplication);
    document.addEventListener('mozfullscreenchange', fixIcebreakerDuplication);

    return () => {
      document.removeEventListener('fullscreenchange', fixIcebreakerDuplication);
      document.removeEventListener('webkitfullscreenchange', fixIcebreakerDuplication);
      document.removeEventListener('mozfullscreenchange', fixIcebreakerDuplication);
    };
  }, []);

  const closeUpgradeModal = () => {
    setUpgradeModal({
      isOpen: false,
      feature: null,
      usageStats: null
    });
  };

  // Router wrapper to detect Google OAuth session_id in URL hash
  const AppRouter = () => {
    const location = window.location;
    
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Check URL hash for session_id (Google OAuth callback)
    // This must be synchronous (during render) to prevent race conditions
    if (location.hash && location.hash.includes('session_id=')) {
      return <AuthCallback />;
    }
    
    return (
      <Routes>
        <Route path="/" element={<EnhancedHomepage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/setup-admin" element={<SetupAdmin />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<PasswordReset />} />
        <Route path="/confirm-email" element={<EmailConfirmation />} />
        <Route path="/resend-confirmation" element={<ResendConfirmation />} />
        <Route path="/delete-account" element={<DeleteAccountRequest />} />
        <Route path="/delete-account/confirm" element={<DeleteAccountConfirm />} />
        <Route path="/profile" element={<ProtectedRoute><ProfileRouter /></ProtectedRoute>} />
        <Route path="/profile/setup" element={<ProtectedRoute><ProfessionalProfileSetup /></ProtectedRoute>} />
        <Route path="/profile/view" element={<ProtectedRoute><ProfileView /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><AccountDashboard /></ProtectedRoute>} />
        <Route path="/profile/detailed" element={<ProtectedRoute><DetailedProfileQuestions onComplete={(data) => console.log('Profile completed:', data)} /></ProtectedRoute>} />
        <Route path="/matching-preferences" element={<ProtectedRoute><MatchingPreferences /></ProtectedRoute>} />
        <Route path="/invite-friends" element={<ProtectedRoute><InviteFriends /></ProtectedRoute>} />
        <Route path="/matches" element={<ProtectedRoute><MatchingInterface /></ProtectedRoute>} />
        <Route path="/connections" element={<ProtectedRoute><YourConnections /></ProtectedRoute>} />
        <Route path="/profile/match/:matchId" element={<ProtectedRoute><MatchProfile /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><Conversations /></ProtectedRoute>} />
        <Route path="/chat/:conversationId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
        <Route path="/video-call" element={<ProtectedRoute><VideoCall /></ProtectedRoute>} />
        <Route path="/speed-dating-session" element={<ProtectedRoute><SpeedDatingDemo /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><BillingDashboard /></ProtectedRoute>} />
        <Route path="/billing/subscription" element={<ProtectedRoute><SubscriptionManager /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route path="/notifications/settings" element={<ProtectedRoute><NotificationPreferences /></ProtectedRoute>} />
        <Route path="/queue" element={<ProtectedRoute><QueuePage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><PersonalAnalyticsDashboard /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><MatchSuccessAnalytics /></ProtectedRoute>} />
        <Route path="/credits" element={<ProtectedRoute><CreditDashboard /></ProtectedRoute>} />
        <Route path="/credits/success" element={<ProtectedRoute><CreditSuccess /></ProtectedRoute>} />
        <Route path="/credits/cancel" element={<ProtectedRoute><CreditCancel /></ProtectedRoute>} />
        <Route path="/matching" element={<ProtectedRoute><MatchingDiscoveryPage /></ProtectedRoute>} />
        <Route path="/ai-date-ideas" element={<ProtectedRoute><AIDateIdeaGenerator /></ProtectedRoute>} />
        <Route path="/speed-dating-demo" element={<SpeedDatingDemo />} />
        <Route path="/phase2-demo" element={<Phase2Demo />} />
        <Route path="/profile-setup-demo" element={<ProfileSetupDemo />} />
        <Route path="/timer-demo" element={<TimerDemo />} />
        <Route path="/how-it-works" element={<EnhancedHowItWorks />} />
        <Route path="/advanced-features" element={<AdvancedFeaturesDemo />} />
        <Route path="/social" element={<ProtectedRoute><SocialMediaIntegration /></ProtectedRoute>} />
        <Route path="/auth/facebook/callback" element={<FacebookAuthCallback />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/ads" element={<AdAnalyticsDashboard />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/community-guidelines" element={<CommunityGuidelines />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/pwa-settings" element={<PWASettings />} />
        <Route path="/subscription/plans" element={<SubscriptionPlans />} />
        <Route path="/subscription-plans" element={<Navigate to="/subscription/plans" replace />} />
        <Route path="/subscription" element={<Navigate to="/subscription/plans" replace />} />
        <Route path="/subscription/success" element={<PaymentSuccess />} />
        <Route path="/subscription/status" element={<ProtectedRoute><SubscriptionStatus /></ProtectedRoute>} />
        <Route path="/boost/success" element={<PaymentSuccess />} />
        <Route path="/coaching" element={<ProtectedRoute><CoachingDashboard /></ProtectedRoute>} />
        <Route path="/coaching/dashboard" element={<ProtectedRoute><CoachingDashboard /></ProtectedRoute>} />
        <Route path="/coaching/module/:moduleId" element={<ProtectedRoute><CoachingModule /></ProtectedRoute>} />
        <Route path="/coaching/modules/:moduleId" element={<ProtectedRoute><CoachingModule /></ProtectedRoute>} />
        <Route path="/coaching/lesson/:lessonId" element={<ProtectedRoute><CoachingLesson /></ProtectedRoute>} />
        <Route path="/coaching/lessons/:lessonId" element={<ProtectedRoute><CoachingLesson /></ProtectedRoute>} />
        <Route path="/coaching/action-plan" element={<ProtectedRoute><ActionPlan /></ProtectedRoute>} />
        <Route path="/premium" element={<ProtectedRoute><PremiumDashboard /></ProtectedRoute>} />
        <Route path="/premium/dashboard" element={<ProtectedRoute><PremiumDashboard /></ProtectedRoute>} />
        <Route path="/premium/optimize" element={<ProtectedRoute><ProfileOptimization /></ProtectedRoute>} />
        <Route path="/premium/analytics" element={<ProtectedRoute><AdvancedAnalytics /></ProtectedRoute>} />
        <Route path="/profile-optimization" element={<ProtectedRoute><ProfileOptimization /></ProtectedRoute>} />
        <Route path="/compatibility-reports" element={<ProtectedRoute><CompatibilityReports /></ProtectedRoute>} />
        <Route path="/compatibility/reports" element={<ProtectedRoute><CompatibilityReports /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  };

  return (
    <HelmetProvider>
      <div className="App overflow-x-hidden w-full max-w-full" style={{maxWidth: '100vw', overflowX: 'hidden'}}>
        <SkipToContent />
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <PWAInstallManager />
            <ReferralHandler />
            <GlobalNavigation />
            <EmailVerificationBanner />
            <main id="main-content" className="min-h-screen overflow-x-hidden max-w-full"
                  role="main" 
                  aria-label="Shot Clock Dating Application"
                  style={{maxWidth: '100vw', overflowX: 'hidden'}}>
              <UpgradeModal />
              <PWAInstallPrompt />
              <OfflineIndicator />
              <div className="min-h-screen flex flex-col overflow-x-hidden max-w-full" style={{maxWidth: '100vw'}}>
                <div className="flex-grow">
                  <AppRouter />
                </div>
                <SocialMediaFooter />
              </div>
            
              {/* Toast Notification Manager - Global notification display */}
              <ToastManager />
            </main>
          </BrowserRouter>
        </AuthProvider>
      </div>
    </HelmetProvider>
  );
}

export default App;